import { Badge, Spinner, cn } from '@up2stay/up2promo-ui';
import { Check } from 'lucide-react';

import type { AutosaveCueStatus } from '@/core/hooks/use-autosave-cue';
import { useDictionary } from '@/core/providers/dictionary-provider';

type AutosaveStatusCueProps = {
  status: AutosaveCueStatus;
  /** Override dictionary labels (e.g. feature-scoped copy). */
  labels?: {
    idle?: string;
    saving?: string;
    saved?: string;
    error?: string;
  };
  className?: string;
};

const VARIANT_BY_STATUS = {
  idle: 'outline',
  saving: 'secondary',
  saved: 'success',
  error: 'destructive',
} as const;

/**
 * Status Board badge for autosave — always visible so identity/header rows
 * keep a stable footprint. Idle shows a quiet persistent hint; saving /
 * saved / error swap copy and tone without collapsing the control.
 */
export function AutosaveStatusCue({
  status,
  labels,
  className,
}: AutosaveStatusCueProps) {
  const dict = useDictionary();
  const autosave = dict.ui?.autosave;

  const label =
    status === 'idle'
      ? (labels?.idle ?? autosave?.idle ?? 'Saves automatically')
      : status === 'saving'
        ? (labels?.saving ?? autosave?.saving ?? 'Saving…')
        : status === 'saved'
          ? (labels?.saved ?? autosave?.saved ?? 'Saved')
          : (labels?.error ?? autosave?.error ?? "Couldn't save");

  return (
    <div className={cn('inline-flex items-center', className)}>
      <Badge
        variant={VARIANT_BY_STATUS[status]}
        className={cn(
          'pointer-events-none gap-1 font-normal',
          status === 'idle' && 'text-muted-foreground',
          status === 'saving' && 'animate-pulse motion-reduce:animate-none',
        )}
        aria-live="polite"
        role="status"
      >
        {status === 'saving' ? (
          <Spinner
            className="size-3 motion-reduce:animate-none"
            role="presentation"
            aria-hidden
          />
        ) : null}
        {status === 'saved' ? <Check className="size-3" aria-hidden /> : null}
        {label}
      </Badge>
    </div>
  );
}
